import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import { getCommentList } from '../../../api/comments';

import { generateCommentCIDKey } from '../utils/generateCommentCIDKey';
import { mergeCommentListWithUserData } from '../../../utils/comments';
import { REQUEST } from '../../../constants/request';
import {
  checkAndSetLoadingWithId,
  handleRequestRejection,
  onMatchedRequest,
} from '../../../utils/asyncThunkRequest';

export const fetchPinnedNestedComments = createAsyncThunk(
  'comments/fetchPinnedNestedComments',
  async (commentsParams, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await getCommentList(
        {
          ...commentsParams,
          params: {
            ...commentsParams.params,
            pinned: true,
          },
        },
        {
          cancelToken: source.token,
        },
      );
      const [commentsResponse, userDataMapResponse] = response;
      return {
        data: mergeCommentListWithUserData(
          commentsResponse,
          userDataMapResponse,
        ),
      };
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: ({ path, params }, { getState }) => {
      const { comments } = getState().comments;
      const commentCIDKey = generateCommentCIDKey({ ...path, ...params });

      return (
        comments.nestedComments.request[commentCIDKey]?.loading !==
        REQUEST.pending
      );
    },
  },
);

export const fetchPinnedNestedCommentsExtraReducers = {
  [fetchPinnedNestedComments.pending]: (state, action) => {
    const { path, params } = action.meta.arg;
    const commentCIDKey = generateCommentCIDKey({ ...path, ...params });

    checkAndSetLoadingWithId(
      state.comments.nestedComments.request,
      action,
      commentCIDKey,
    );
  },
  [fetchPinnedNestedComments.fulfilled]: (state, action) => {
    const { path, params } = action.meta.arg;
    const commentCIDKey = generateCommentCIDKey({ ...path, ...params });

    onMatchedRequest(
      state.comments.nestedComments.request[commentCIDKey],
      action,
      () => {
        action.payload.data.comments.forEach(comment => {
          const currentCommentCIDKey = generateCommentCIDKey(comment);
          state.comments.comment.byId[currentCommentCIDKey] = comment;
          if (!state.comments.comment.allIds.includes(currentCommentCIDKey)) {
            state.comments.comment.allIds.push(currentCommentCIDKey);
          }
          if (!state.comments.item.byId[commentCIDKey]) {
            state.comments.item.byId[commentCIDKey] = [];
          }
          if (
            !state.comments.parent.byId[commentCIDKey].includes(
              currentCommentCIDKey,
            )
          ) {
            state.comments.parent.byId[commentCIDKey].unshift(
              currentCommentCIDKey,
            );
          }
          if (
            !state.comments.parent.byId[currentCommentCIDKey] &&
            comment.metadata.replies > 0
          ) {
            state.comments.parent.byId[currentCommentCIDKey] = [];
          }
        });
      },
    );
  },
  [fetchPinnedNestedComments.rejected]: (state, action) => {
    const { path, params } = action.meta.arg;
    const commentCIDKey = generateCommentCIDKey({ ...path, ...params });

    handleRequestRejection(
      state.comments.nestedComments.request[commentCIDKey],
      action,
    );
  },
};
